/**
 * Shared Stripe Elements + PaymentElement options (aligned with checkout card payment in Payment.jsx).
 */

export const STRIPE_CHECKOUT_PAYMENT_ELEMENT_CONTAINER_CLASS =
  "border border-gray-200 rounded-lg p-4 checkout-payment-element-wrap";

const STRIPE_LOCALE = "en";

/** Set when /payment/stripe/key loads (used by Elements for dashboard payment-method config). */
let cachedPaymentMethodConfigurationId = "";

export function cacheStripeKeyResponse(data) {
  cachedPaymentMethodConfigurationId = String(
    data?.paymentMethodConfigurationId || ""
  ).trim();
}

export function getCachedPaymentMethodConfigurationId() {
  return cachedPaymentMethodConfigurationId;
}

/** Prefer E.164 for GB (Stripe Link / wallets handle it better). */
export function formatPhoneForStripe(phone) {
  if (!phone) return "";
  const trimmed = phone.trim();
  if (trimmed.startsWith("+")) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("44") && digits.length >= 10) return `+${digits}`;
  if (digits.startsWith("0") && digits.length >= 10) return `+44${digits.slice(1)}`;
  return trimmed;
}

/**
 * Prefill Link / wallets from checkout payload and/or logged-in profile.
 * @param {object|null|undefined} orderData - e.g. latestOrder.user
 * @param {object|null|undefined} accountUser - Redux user
 */
export function buildStripeDefaultBilling(orderData, accountUser) {
  const fromCheckout =
    orderData?.user && typeof orderData.user === "object" ? orderData.user : {};
  const u = accountUser && typeof accountUser === "object" ? accountUser : {};
  const name = String(fromCheckout.name || u.name || u.username || "").trim();
  const email = String(fromCheckout.email || u.email || "").trim();
  let rawPhone = fromCheckout.phoneNumber ?? u.phoneNumber;
  if (typeof rawPhone === "number" && Number.isFinite(rawPhone)) {
    rawPhone = String(rawPhone);
  }
  const phone = formatPhoneForStripe(String(rawPhone || "").trim());
  const billingDetails = {};
  if (name) billingDetails.name = name;
  if (email) billingDetails.email = email;
  if (phone) billingDetails.phone = phone;
  return Object.keys(billingDetails).length ? billingDetails : null;
}

/** Remount Elements when billing prefill changes (matches Payment.jsx). */
export function getStripeBillingPrefillKey(billingDefaults) {
  if (!billingDefaults) return "";
  return [
    billingDefaults.name || "",
    billingDefaults.email || "",
    billingDefaults.phone || "",
  ].join("|");
}

/**
 * Options for <Elements> (clientSecret mode).
 * @param {{ clientSecret: string, billingDefaults?: object|null, paymentMethodConfiguration?: string|null }} params
 */
export function getStripeElementsOptions({
  clientSecret,
  billingDefaults = null,
  paymentMethodConfiguration = null,
}) {
  const pmc =
    String(paymentMethodConfiguration || "").trim() ||
    getCachedPaymentMethodConfigurationId();
  return {
    clientSecret,
    appearance: { theme: "stripe" },
    locale: STRIPE_LOCALE,
    business: { name: "Veteran Airsoft Marketplace" },
    ...(pmc ? { paymentMethodConfiguration: pmc } : {}),
    ...(billingDefaults && Object.keys(billingDefaults).length
      ? { defaultValues: { billingDetails: billingDefaults } }
      : {}),
  };
}

/**
 * Options for <PaymentElement /> — accordion layout + billing fields (matches checkout).
 * @param {object|null} defaultBillingDetails - from buildStripeDefaultBilling
 */
/** Express Checkout Element — `always` shows Google/Apple Pay when the browser supports them. */
export function getStripeExpressCheckoutElementOptions() {
  return {
    buttonHeight: 48,
    buttonType: {
      googlePay: "pay",
      applePay: "buy",
    },
    paymentMethods: {
      applePay: "always",
      googlePay: "always",
      link: "auto",
    },
  };
}

/**
 * Payment Element options (Card tab also shows Google Pay / Apple Pay when eligible).
 */
export function getStripePaymentElementOptions(defaultBillingDetails = null) {
  return {
    layout: {
      type: "tabs",
      defaultCollapsed: false,
    },
    wallets: {
      applePay: "auto",
      googlePay: "auto",
    },
    ...(defaultBillingDetails && Object.keys(defaultBillingDetails).length
      ? { defaultValues: { billingDetails: defaultBillingDetails } }
      : {}),
    fields: {
      billingDetails: {
        name: "auto",
        email: "auto",
        phone: "auto",
      },
    },
  };
}
